import { ActivationPackService } from '@/services/database/ActivationPackService';
import {
  ActivationPackWithTarget,
  ASSET_TYPE_INFO,
  GeneratedAssetType,
} from '@/types/activation/activation-pack';
import { PrintOrder, PrintOrderItem } from '@/types/activation/print-order';

/**
 * A print-ready spec sheet for customers who don't have a print vendor.
 *
 * It is a self-contained, printable HTML page built entirely in the browser
 * from the order the customer already has: every line item, its quantity, its
 * production file link, and the physical spec we know from the pack's own
 * settings. Take it to any local print shop and they can quote from it.
 *
 * No backend involved, and no invented facts: anything we don't actually know
 * (paper stock, finish, delivery) is printed as a question for the printer
 * rather than a claim.
 */

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const STICKER_SIZE_LABELS: Record<string, string> = {
  '2x2': '50 × 50 mm (2 × 2 in)',
  '3x3': '75 × 75 mm (3 × 3 in)',
  '4x4': '100 × 100 mm (4 × 4 in)',
};

/** What we genuinely know about the physical shape of each artwork type. */
const specFor = (
  assetType: GeneratedAssetType | null | undefined,
  pack: ActivationPackWithTarget,
): string => {
  switch (assetType) {
    case 'sticker_pdf': {
      const size = pack.asset_config?.sticker_size;
      const shape = pack.asset_config?.sticker_shape;
      const sizeLabel =
        (size && STICKER_SIZE_LABELS[size]) ||
        (pack.asset_config?.sticker_custom_width && pack.asset_config?.sticker_custom_height
          ? `${pack.asset_config.sticker_custom_width} × ${pack.asset_config.sticker_custom_height} in`
          : 'See artwork file');
      return `Sticker · ${sizeLabel}${shape ? ` · ${shape} corners` : ''} · vinyl recommended for outdoor use`;
    }
    case 'poster_pack_pdf':
    case 'app_poster_pdf':
      return 'Poster · A4 portrait (210 × 297 mm) · full colour, single sided';
    case 'lookbook_pdf':
      return 'Booklet page · A4 portrait (210 × 297 mm) · full colour';
    case 'app_poster_png':
      return 'Poster artwork (image) · A4 portrait proportions · full colour';
    default:
      return 'See the artwork file for size and bleed';
  }
};

const itemRow = (item: PrintOrderItem, pack: ActivationPackWithTarget): string => `
  <tr>
    <td>
      <div class="label">${escapeHtml(item.label)}</div>
      ${item.notes ? `<div class="muted">${escapeHtml(item.notes)}</div>` : ''}
      <div class="muted">${escapeHtml(
        item.assetType ? ASSET_TYPE_INFO[item.assetType]?.description || '' : 'Attached artwork',
      )}</div>
    </td>
    <td class="qty">${item.quantity}</td>
    <td>${escapeHtml(specFor(item.assetType, pack))}</td>
    <td class="link"><a href="${escapeHtml(item.url)}">${escapeHtml(item.url)}</a></td>
  </tr>`;

export const buildPrintSpecSheetHtml = (
  pack: ActivationPackWithTarget,
  order: Pick<PrintOrder, 'items' | 'notes'>,
): string => {
  const targetName = ActivationPackService.getTargetName(pack);
  const totalUnits = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const generatedOn = new Date().toLocaleDateString();

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Print specification — ${escapeHtml(pack.name)}</title>
<style>
  :root { color-scheme: light; }
  body { font-family: Poppins, "Segoe UI", Arial, sans-serif; color: #222B45; margin: 0; padding: 32px; background: #F9FAFC; }
  .sheet { max-width: 900px; margin: 0 auto; background: #fff; border: 1px solid #E5EAF0; border-radius: 16px; padding: 32px; }
  .bar { height: 6px; border-radius: 999px; background: linear-gradient(90deg, #4E9CFF, #FF4DB8); margin-bottom: 24px; }
  h1 { font-size: 24px; margin: 0 0 4px; }
  h2 { font-size: 15px; text-transform: uppercase; letter-spacing: .06em; color: #64748B; margin: 28px 0 10px; }
  .sub { color: #64748B; font-size: 14px; margin: 0 0 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; color: #64748B; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #E5EAF0; padding: 8px 10px 8px 0; }
  td { border-bottom: 1px solid #F1F5F9; padding: 12px 10px 12px 0; vertical-align: top; }
  .label { font-weight: 600; }
  .muted { color: #64748B; font-size: 12px; margin-top: 2px; }
  .qty { font-weight: 700; white-space: nowrap; }
  .link a { color: #4E9CFF; word-break: break-all; font-size: 11px; }
  ul { padding-left: 18px; margin: 0; color: #334155; font-size: 13px; line-height: 1.9; }
  .totals { margin-top: 16px; font-size: 14px; font-weight: 600; }
  .note { margin-top: 10px; background: #F9FAFC; border: 1px solid #E5EAF0; border-radius: 12px; padding: 12px 14px; font-size: 13px; }
  @media print { body { background: #fff; padding: 0; } .sheet { border: 0; padding: 0; } }
</style>
</head>
<body>
  <div class="sheet">
    <div class="bar"></div>
    <h1>Print specification</h1>
    <p class="sub">${escapeHtml(pack.name)} · ${escapeHtml(targetName)}</p>
    <p class="sub">Prepared ${escapeHtml(generatedOn)}</p>

    <h2>What to print</h2>
    <table>
      <thead>
        <tr><th>Item</th><th>Qty</th><th>Specification</th><th>Artwork file</th></tr>
      </thead>
      <tbody>
        ${order.items.map((item) => itemRow(item, pack)).join('')}
      </tbody>
    </table>
    <p class="totals">${order.items.length} ${order.items.length === 1 ? 'item' : 'items'} · ${totalUnits} total units</p>

    ${order.notes ? `<div class="note"><strong>Special instructions:</strong> ${escapeHtml(order.notes)}</div>` : ''}

    <h2>Please confirm before printing</h2>
    <ul>
      <li>Final proof of each artwork file</li>
      <li>Paper or vinyl stock, and finish (matt / gloss / weatherproof)</li>
      <li>Price per item and total</li>
      <li>Lead time</li>
      <li>Delivery address and shipping cost</li>
    </ul>

    <h2>About the artwork</h2>
    <ul>
      <li>Every file above is print-ready and full colour.</li>
      <li>Each QR code points at a live page — please do not resize below 25 mm or crop it.</li>
      <li>Scan-test one printed proof before the full run.</li>
    </ul>
  </div>
</body>
</html>`;
};

const slugify = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'activation-pack';

/** Build the spec sheet and hand it to the browser as a download. */
export const downloadPrintSpecSheet = (
  pack: ActivationPackWithTarget,
  order: Pick<PrintOrder, 'items' | 'notes'>,
): void => {
  const blob = new Blob([buildPrintSpecSheetHtml(pack, order)], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${slugify(pack.name)}-print-specification.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
};
